const federatedSchema = `
enum ActiveStatus {
  active
  inactive
}

type AgreementPage {
  enabled: Boolean!
  id: ID!
  position: Int!
}

type Alert {
  message: String
  title: String
}

type Announcement {
  createdAt: DateTime
  description: String!
  id: ID!
  isArchived: Boolean!
  link: URL
  location: Location!
  title: String!
}

type ApprovalStatus {
  autoApproved: Boolean!
  reviewNote: String
  reviewedAt: DateTime
  reviewerId: ID
  reviewerName: String
  status: String!
}

"""
Wrapper for returning available floors for location. Errors only populated if desks are requested but not available.
"""
type AvailableDesksOnFloor {
  desks: [Desk!]!
  errors: [EnvoyError!]
}

"""
Wrapper for returning available floors for location. Errors only populated if desks are requested but not available.
"""
type AvailableFloors {
  errors: [EnvoyError!]
  floors: [Floor!]!
}

input BeaconIdInput {
  major: Int!
  minor: Int!
}

input BeaconInput {
  id: BeaconIdInput!
  proximity: BeaconProximity!
  rssi: Int!
}

enum BeaconProximity {
  far
  immediate
  near
  unknown
}

type BulkEmployeesByEmail {
  employees: [Employee!]!
}

type BulkRegistrationPDResponse {
  errors: [EnvoyError!]
  registrationDates: [RegistrationDatePartialDay!]!
}

type BulkRegistrationResponse {
  errors: [EnvoyError!]
  registrationDates: [RegistrationDate!]!
}

"""
Represents either an Invite, Entry or Delivery
"""
union CardUnion = Delivery | Entry | Invite

type Carrier {
  id: ID!

  """
  Full carrier name, like 'US Postal Service'
  """
  name: String!

  """
  Short, unique identifer, like 'usps'
  """
  slug: String
}

type Color {
  hexValue: String!
}

type ColorPalette {
  accent: Color!
  accentText: Color!
}

"""
An ISO 8601-encoded date
"""
scalar Date

type Delivery {
  alias: String
  canEdit: Boolean!
  carrier: Carrier
  deliveryArea: DeliveryArea!
  id: ID!
  imageFull: ImageURISource
  imageThumb: ImageURISource
  lastNotificationSentAt: DateTime
  links: DeliveryLinks!
  meta: DeliveryMeta
  notes: String
  notificationSentAt: DateTime
  pickedUpAt: DateTime
  receivedAt: DateTime!
  recipient: Recipient
  skipRecipientNotification: Boolean
  status: String!
  trackingNumber: String
}

type DeliveryArea {
  canAdd: Boolean!
  deliveries(
    """
    Number of results returned per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int

    """
    Column to sort on, following JSON:API's convention. Eg 'sort=-status,created_at'
    """
    sort: String

    """
    Filter by delivery status
    """
    status: [DeliveryStatus!]!
  ): [Delivery!]!
  failedDeliveriesCount: Int!
  id: ID!
  name: String!
  pendingDeliveriesCount: Int!
  pendingOwnDeliveriesCount: Int!
  realm: Realm @deprecated(reason: "Legacy concept from Identity")
  requireSignature: Boolean!
  status: ActiveStatus!
  totalDeliveriesCount: Int!
}

type DeliveryLinks {
  updateImage: URL!
  uploadImage: URL!
}

type DeliveryMeta {
  permissions: DeliveryPermissions!
}

type DeliveryPermissions {
  createNotes: Boolean!
  deleteRecipientEmployeeId: Boolean!
}

"""
Delivery statuses have the following lifecycle

'''
new --> processing +--------> pending --> collected
                   |             ^
                   |             |
                   +--> failed --+
'''

When the deliveries mobile app takes a photo of a label, the client
creates a "shell" Delivery with the initial status 'new' as a
container to upload the label image to. Once the image has been
uploaded, it changes status to 'processing' while it waits for OCR to
complete. Once it receives OCR results, the backend attempts to find
a match. If successful, it goes to 'pending' state and notifications are
sent out. If no match, it transitions to 'failed'. Finally, when an
employee picks up their package, the delivery transitions to its final
resting state, 'collected'.
"""
enum DeliveryStatus {
  """
  Picked up deliveries
  """
  collected

  """
  Failed to find a match
  """
  failed

  """
  Initial status
  """
  new

  """
  Deliveries ready to be collected
  """
  pending

  """
  Waiting for match results
  """
  processing
}

"""
Desk availability values
"""
enum DeskAvailabilityEnum {
  """
  Desk available for reservation
  """
  available

  """
  Desk not available for reservation
  """
  unavailable

  """
  Desk currently assigned to this user
  """
  userAssigned
}

"""
Desk Status
"""
enum DeskStatusEnum {
  """
  a desk that has been reserved by an employee who has hidden their schedule, if
  a location has disabled schedule sharing then all desks that are booked should
  be returned as anonymous
  """
  anonymous

  """
  a desk that does not have a reservation for the given time
  """
  available

  """
  a desk that has been reserved by an employee with a shared schedule
  """
  booked

  """
  A desk that has been permanently assigned to someone with schedule sharing on
  """
  permanentlyAssigned

  """
  A desk that has been permanently assigned to someone with schedule sharing off
  """
  permanentlyAssignedPrivate

  """
  a desk that cannot be used by the user querying because of neighborhood restrictions
  """
  unavailable
}

type DesksConfig {
  companyId: ID!
  desksLocations: [DesksLocation!]
}

type DesksLocation {
  active: Boolean!
  autoAssignDesk: Boolean!
  checkInDuration: Int
  checkInRequiredBy: String
  id: ID!
  workplaceDays: [WorkplaceDay!]
}

type DesksOnFloor {
  desks: [Desk!]!
  errors: [EnvoyError!]
}

"""
Direct upload attributes
"""
type DirectUpload {
  """
  Created blob record ID
  """
  blobId: ID!
  byteSize: Int
  checksum: String
  contentType: String
  createdAt: String
  filename: String

  """
  HTTP request headers (JSON-encoded)
  """
  headers: [String!]!
  key: String

  """
  Created blob record signed ID
  """
  signedBlobId: ID!

  """
  Upload URL
  """
  url: String!
}

input DocumentAttachmentSignedBlob {
  file: String!
  userDocumentTemplateAttachmentId: ID!
}

type Door {
  """
  This is used to store beacon-related data properties. Used by mobile clients to match a nearby beacon
  """
  doorBeacon: DoorBeacon!
  id: ID!
  name: String!
  type: String!
}

type DoorBeacon {
  major: Int
  minor: Int
  uuids: [String!]
}

"""
Wrapper for returning employee desks. Errors only populated if reservation request.
"""
type EmployeeDesks {
  errors: [EnvoyError!]
  reservations: [Reservation!]!
}

type EmployeeFavorite {
  favorite: Employee!
  id: ID!
  owner: Employee!
}

"""
Invite for employee registration only, no visitor invites.
"""
type EmployeeInvite {
  delegatedBooker: Employee
  id: ID!
}

"""
DEPRECATED: Use EmployeeRegistrationPartialDayType instead
"""
type EmployeeRegistration {
  """
  (DEPRECATED) Future dates within the location's open registration window
  """
  registrationDates: [RegistrationDate!]! @deprecated(reason: "Use EmployeeRegistrationPartialDayType instead")
}

"""
Wrapper for multi-day registration and invites data
"""
type EmployeeRegistrationPartialDay {
  """
  Future dates within the location's open registration window
  """
  registrationDates: [RegistrationDatePartialDay!]!
}

type Entry {
  additionalGuests: Int!
  agreementRefused: Boolean
  desk: Desk
  employee: Employee
  flow: Flow
  flowName: String!
  host: String @deprecated(reason: "Moved into Employee type, use 'Employee.name' instead.")
  hostEmail: String @deprecated(reason: "Moved into Employee type, use 'Employee.email' instead.")
  id: ID!
  isDelivery: Boolean!
  location: Location
  nda: String
  passportStamp: PassportStamp
  photoUrl: String
  privateNotes: String
  signedInAt: DateTime!
  signedInVia: String!
  signedOutAt: DateTime
  userData: [FormField!]!
  visitor: Visitor!
}

"""
Available entry scopes are 'mine' or 'boss'
"""
enum EntryScopeEnum {
  """
  This scope is for fetching my entries and my bosses entries given I am an assistant
  """
  boss

  """
  This value is for fetching only my entries if I am an assistant
  """
  mine
}

enum EntryStatusEnum {
  signedIn
  signedOut
}

"""
Wrapper for account configs for various envoy products
"""
type EnvoyAccountConfig {
  desksConfig: DesksConfig!
}

type EnvoyError {
  code: Int!
  domain: String!
  meta: [ErrorMetaData!]
}

type ErrorMetaData {
  key: String!
  value: String!
}

type Feed {
  group: String!
  id: ID!
  token: String!
}

type FloorReservationCount {
  id: ID!
  reservationCount: Int!
}

type FloorReservationCounts {
  errors: [EnvoyError!]
  floorReservationCounts: [FloorReservationCount!]!
}

type Flow {
  additionalGuests: Boolean!
  agreementPage: AgreementPage
  employeeCentric: Boolean
  enabled: Boolean!
  id: ID!
  localizedName: String
  name: String!
  position: Int!
  signInFieldPage: SignInFieldPage!
}

type FormField {
  field: String!
  value: String
}

"""
Invite form field
"""
input FormFieldInput {
  """
  Field
  """
  field: String!

  """
  Value
  """
  value: String!
}

type GenericResponse {
  message: String!
  success: Boolean!
}

"""
Wrapper for returning reservations matching the given filters
"""
type GetReservations {
  errors: [EnvoyError!]
  reservations: [Reservation!]!
}

type ImageURISource {
  uri: URL!
}

"""
User document input field
"""
type InputField {
  """
  For date types, this will be in the YYYY-MM-DD format
  """
  defaultValue: String
  identifier: String!
  required: Boolean!
  title: String!
  type: InputFieldTypeEnum!
}

"""
User document input field data
"""
type InputFieldData {
  """
  Field identifier
  """
  identifier: String!

  """
  Field value. See InputFieldTypeEnum for formatting.
  """
  value: String!
}

"""
User document input field data
"""
input InputFieldDataInput {
  """
  Field identifier
  """
  identifier: String!

  """
  Field value. See InputFieldTypeEnum for formatting.
  """
  value: String!
}

"""
User document input field types
"""
enum InputFieldTypeEnum {
  """
  Date field values format: YYYY-MM-DD
  """
  date
}

type IntegrationSetting {
  id: ID!
  integrationDescription: String!
  integrationId: String!
  integrationName: String!
  integrationStatus: String!
  purpose: String!
}

type Invite {
  additionalGuests: Int!
  approvalStatus: ApprovalStatus
  arrived: Boolean!
  email: String @deprecated(reason: "Moved into Visitor type, use 'Visitor.email' instead.")
  employee: Employee
  employeeInvite: EmployeeInvite
  entry: Entry
  entryId: ID
  entrySignedOutAt: DateTime
  expectedArrivalTime: DateTime!
  flow: Flow
  fullName: String! @deprecated(reason: "Moved into Visitor type, use 'Visitor.fullName' instead.")
  guestUpdatedAt: DateTime
  hostName: String
  id: ID!
  isPresigned: Boolean!
  location: Location
  phone: String
  preregistrationLink: String

  """
  A pre-reg link for the invite's approval status.
  Currently this page only displays employee registration denial messaging. In
  the future it may include more general status info. Unlike the
  'preregistrationLink' this page can be accessed after preregistration is complete.
  """
  preregistrationStatusLink: String
  privateNotes: String
  touchlessSignInAvailableTime: DateTime
  touchlessSignInPresenceRequired: Boolean!
  userData: [FormField!]!
  userDocumentList: [InviteUserDocumentData!]!
  visitor: Visitor!
}

type InviteReservationResponse {
  errors: [EnvoyError!]
  invite: Invite
  reservation: Reservation
}

type InviteUserDocumentData {
  documentApprovalStatusForLocation: String
  title: String!
  userDocumentId: ID
  userDocumentTemplateId: ID!
}

type Location {
  address: String
  addressLineOne: String
  addressLineTwo: String
  capacityLimit: Int
  companyName: String
  deliveryAreas: [DeliveryArea!]
  employeeScheduleEnabled: Boolean!
  employeeScreeningEnabled: Boolean
  feeds: [Feed!]!
  flows: [Flow!]!
  id: ID!
  latitude: Float
  logoUrl: String
  longitude: Float
  name: String!
  nearVisitScreeningEnabled: Boolean!
  preRegistrationEnabled: Boolean!
  role: UserRole
  scheduleEligibilityOffset: Int
  subscription: ProductSubscription!
  theme: LocationTheme!
  ticketConfiguration: TicketConfiguration!
  timezone: String!
  companyId: ID
  """
  Location's company using data loader
  """
  company: Company

  """
  Location's rooms using data loader
  """
  rooms: [Room!]!

  """
  Whether or not rooms device notifications should be sent to global admins at this location
  """
  roomsShouldEmailGlobalAdmins: Boolean!
  """
  Whether or not rooms device notifications should be sent to location admins at this location
  """
  roomsShouldEmailLocationAdmins: Boolean!
  """
  Additional rooms notification emails (these may be email addresses unknown to other apps in Envoy)
  """
  roomsNotificationEmails: [String!]
  """
  A flag indicating whether this location's room resizer setting differs from the company-wide setting
  If this is null, defer to the setting on the company
  """
  roomResizerEnabledOverride: Boolean
  """
  A flag indicating whether this location's onsite room recapture setting differs from the company-wide setting
  If this is null, defer to the setting on the company
  """
  onsiteRoomRecaptureEnabledOverride: Boolean
  """
  A coalesced version of roomResizerEnabledOverride from the location object and roomResizerEnabled from the company object.
  Indicates whether the room resizer feature is turned on for this location.
  """
  roomResizerEnabled: Boolean!
  """
  A coalesced version of onsiteRoomRecaptureEnabledOverride from the location object and onsiteRoomRecaptureEnabled from the company object.
  Indicates whether the onsite room recapture feature is turned on for this location.
  """
  onsiteRoomRecaptureEnabled: Boolean!
  """
  Time window (in seconds) before meetings start, when users can check in
  """
  roomsPreCheckInWindow: Int!
  roomsPreCheckInWindowIsOverride: Boolean!
  """
  Time window (in seconds) after meetings start, when users can check in
  """
  roomsCheckInWindow: Int!
  roomsCheckInWindowIsOverride: Boolean!

  """
  A flag indicating whether rooms at this location have check-in enabled
  """
  roomCheckInEnabled: Boolean!
}

type LocationTheme {
  logo: URL
  palette: ColorPalette!
}

type Mutation {
  """
  Accepts the current privacy policy for the signed in user
  """
  acceptPrivacyPolicy: GenericResponse!
  acceptWorkplaceVisitRequest(workplaceVisitRequestId: ID!): Invite!

  """
  Adds an alias to a recipient so that future transcriptions that match the alias will find the right recipient
  """
  addAlias(
    """
    Delivery ID
    """
    delivery: ID!

    """
    The alias, e.g. Bob, Bobby, Robbie
    """
    nickname: String!

    """
    The employee ID
    """
    recipient: String!
  ): Delivery!

  """
  Add a new delivery to a delivery area
  """
  addDelivery(
    """
    Carrier ID
    """
    carrier: ID

    """
    Delivery area ID
    """
    deliveryArea: ID!

    """
    Delivery notes
    """
    notes: String

    """
    Recipient ID
    """
    recipient: ID!

    """
    Skip recipient notification
    """
    skipRecipientNotification: Boolean

    """
    Delivery's tracking number
    """
    trackingNumber: String
  ): Delivery!
  addInvite(
    additionalGuests: Int
    email: String

    """
    Employee ID
    """
    employee: ID
    expectedArrivalTime: DateTime!
    fullName: String!

    """
    Location ID
    """
    location: ID!
    notifyVisitor: Boolean
    phone: String
    privateNotes: String
    userData: [FormFieldInput!]!
  ): Invite!
  addMembersToWorkplaceGroup(employeeIds: [ID!]!, groupId: ID!): WorkplaceGroup!
  addTicket(
    attachments: [String!]
    categoryId: ID!
    description: String!
    locationDescription: TicketLocationDescriptionInput
    locationId: ID!
    priority: TicketPriorityEnum!
    requestType: TicketRequestTypeEnum!
    subcategory: String
  ): Ticket!
  addUser(
    acceptedPrivacyPolicy: Boolean
    companyName: String
    email: String!
    fullName: String!
    password: String
    phoneNumber: String
  ): NewUser!

  checkInReservation(
    reservationId: ID!

    """
    Sign in method
    """
    signInMethod: String
  ): Reservation!
  createDeskReservation(
    deskId: ID

    """
    If no invite_id is passed, this invite object is required.
    """
    invite: NewInviteInput
    inviteId: ID
  ): ReservationResponse!

  """
  Direct upload mutation to allow clients to directly upload files
  """
  createDirectUpload(
    byteSize: Int!
    checksum: String!
    contentType: String!
    fileName: String!

    """
    S3 bucket prefix
    """
    prefix: String
  ): DirectUpload!
  createInviteReservation(
    deskId: ID

    """
    If no invite_id is passed, this invite object is required.
    """
    invite: NewInviteInput
    inviteId: ID
  ): InviteReservationResponse!

  """
  Creates user's notification preference
  """
  createNotificationPreference(
    notificationPlatformKey: String!
    notificationType: String!
    preferenceValue: Boolean!
    relationId: ID!
    relationType: String!
  ): NotificationPreference!
  createUserDocument(
    inputFieldsData: [InputFieldDataInput!]
    signedBlobs: [DocumentAttachmentSignedBlob!]!
    userDocumentTemplateId: ID!
  ): UserDocument!
  createWorkplaceGroup(members: [ID!]!, name: String!): WorkplaceGroup!
  createWorkplaceVisitRequest(date: Date!, employeeIds: [ID!]!, locationId: ID!): WorkplaceVisitRequestCreateResponse!
  deactivateUserAccount: GenericResponse!
  declineWorkplaceVisitRequest(workplaceVisitRequestId: ID!): GenericResponse!

  """
  Deletes multiple deliveries
  """
  deleteDeliveries(
    """
    Delivery ID
    """
    id: [ID!]!
  ): [Delivery!]!

  """
  Deletes a delivery
  """
  deleteDelivery(
    """
    Delivery ID
    """
    id: ID!
  ): Delivery!

  """
  Deletes an invite
  """
  deleteInvite(
    """
    Invite ID
    """
    id: ID!
  ): Invite!
  deleteUserPhoto: User!
  deleteWorkplaceGroup(groupId: ID!): GenericResponse!

  """
  Edit a delivery
  """
  editDelivery(
    """
    Carrier ID
    """
    carrier: ID

    """
    Delivery area ID
    """
    deliveryArea: ID!

    """
    Delivery ID
    """
    id: ID!

    """
    Delivery notes
    """
    notes: String

    """
    Recipient ID
    """
    recipient: ID!

    """
    Skip recipient notification
    """
    skipRecipientNotification: Boolean

    """
    Delivery's tracking number
    """
    trackingNumber: String
  ): Delivery!
  editInvite(
    additionalGuests: Int
    email: String
    expectedArrivalTime: DateTime
    fullName: String

    """
    Invite ID
    """
    id: ID!
    privateNotes: String
    userData: [FormFieldInput!]
  ): Invite!

  """
  Updates user's profile
  """
  editProfile(formattedName: String, phoneNumber: String): User!
  editWorkplaceGroup(id: ID!, name: String): WorkplaceGroup!

  """
  Mark one announcement as seen
  """
  markAnnouncementAsSeen(
    """
    Announcement ID
    """
    announcementId: ID!
  ): GenericResponse!

  """
  Mark multiple deliveries as picked up
  """
  markDeliveriesAsPickedUp(
    """
    Deliveries IDs
    """
    id: [ID!]!

    """
    If provided, the deliveries will be marked as picked up at the specified time.
    Otherwise, the current time will be used.
    """
    pickedUpAt: DateTime
  ): [Delivery!]!

  """
  Mark a single delivery as picked up
  """
  markDeliveryAsPickedUp(
    """
    Delivery ID
    """
    id: ID!

    """
    If provided, the delivery will be marked as picked up at the specified time.
    Otherwise, the current time will be used.
    """
    pickedUpAt: DateTime
  ): Delivery!
  markEmployeeAsFavorite(targetEmployeeId: ID!): EmployeeFavorite!
  notifyBuilding(locationId: ID!, userId: ID!): GenericResponse!
  registerAndCheckIn(invite: NewInviteInput!): SimplifiedSignInUnion!
  registerAndCheckInToInvite(inviteId: ID!): SimplifiedSignInUnion!
  registerPushNotificationToken(appVersion: String, deviceName: String!, id: ID!, tokenType: String): GenericResponse!
  releaseDeskReservation(reservationId: ID!): GenericResponse!
  releaseDeskReservations(reservationIds: [ID!]!): GenericResponse!
  removeMembersFromWorkplaceGroup(employeeIds: [ID!]!, groupId: ID!): WorkplaceGroup!

  """
  Forces a notification to be resent to the recipient
  """
  resendDeliveryNotification(
    """
    Delivery ID
    """
    id: ID!
  ): Delivery!

  """
  Triggers reset password email
  """
  resetPassword(email: String!): PasswordReset!
  signInInvite(
    """
    Invite ID
    """
    inviteId: ID!

    """
    Notify host
    """
    notifyHost: Boolean

    """
    Print badges
    """
    printBadges: Boolean

    """
    Reservation ID
    """
    reservationId: ID

    """
    Sign in method
    """
    signInMethod: String
  ): [Entry!]!
  signOutEntry(id: ID!, signedOutAt: DateTime!, signedOutBy: String): Entry!
  unlockDoor(
    doorId: ID
    localName: String
    locationId: ID!
    major: Int
    minor: Int
    type: String!
    uuid: String
  ): GenericResponse!
  unmarkEmployeeAsFavorite(targetEmployeeId: ID!): GenericResponse!
  updateAvatar(avatar: Upload!): User!
  updateDeskReservation(deskId: ID!, entryId: ID!): GenericResponse!
    @deprecated(reason: "Use ChangeDeskReservation instead")
  updateEmployeeSchedulePreference(value: Boolean!): User!
  updateHostPushPreference(value: Boolean!): User!
  updateInvitesPushPreference(value: Boolean!): User!

  """
  Updates user's notification preference
  """
  updateNotificationPreference(id: ID!, value: Boolean!): NotificationPreference!
  updatePassportOwnLocations(value: Boolean!): User!
  updateUserDocument(
    inputFieldsData: [InputFieldDataInput!]
    signedBlobs: [DocumentAttachmentSignedBlob!]!
    userDocumentId: ID!
  ): UserDocument!
  updateUserNotificationPreference(feature: String!, setting: String!, value: Boolean!): User!
    @deprecated(reason: "use Mutations::BulkUpdateUserNotificationPreference instead")
  updateUserPhoto(signedBlobId: ID!): User!
  uploadTicketAttachments(attachments: [Upload!]!, ticketId: ID!): GenericResponse!

  createCompany(input: CompanyInput!): Company!
  unlinkConnection(connectionId: ID!, shouldDeleteAssociatedRooms: Boolean! = false): Company!
  synchronizeCompanyRoomList: ErrorList!
  updateCompanyConfig(input: CompanyConfigInput!): Company!
  linkDeviceWithRoom(deviceId: ID!, roomId: ID!): Device
  pairDevice(pairingCode: String!): DeviceToken!
  unpairDevice(id: ID!): String!
  updateLightedCaseType(deviceId: ID, clientId: String, caseType: LightedCaseType!): Device
  linkRoomsWithLocation(
    roomIds: [ID] = []
    calendarIds: [ID] = []
    calendarEmails: [String] = []
    locationId: ID
  ): [Room!]!
  resyncRoomCalendar(id: ID!): Room!
  syncRoomCalendar(id: ID!): Room!
  updateRoomConfig(input: RoomConfigInput!): Room!
  updateRoomCheckInWindow(input: RoomCheckInWindowInput!): Room!
  updateLocationRoomsCheckInWindow(locationId: ID!, roomsPreCheckInWindow: Int!, roomsCheckInWindow: Int!): Location!
  deleteRoomBackgroundImage(roomId: ID!): Room!
  createLocation(input: LocationInput!): Location!
  checkInCalendarEvent(input: CheckInInput!): CalendarEvent!
  checkOutCalendarEvent(input: CheckOutInput!): CalendarEvent!
  createCalendarEvent(input: CalendarEventInput!): CalendarEvent!
  sendSlackCheckIn(input: SendSlackCheckInInput!): Boolean!
  markEventMissed(input: MarkEventMissedInput!): CalendarEvent!
  extendCalendarEvent(input: ExtendEventInput!): CalendarEvent!
  deleteCalendarEvent(input: DeleteEventInput!): CalendarEvent!
  getDevicePairingCode: DevicePairingCode!
  getDeviceToken(deviceCode: String!): DeviceToken!
  approveDeviceUserCode(userCode: String!, roomId: String!): DevicePairingStatus!
  pingWebviewLivenessCheck(clientDeviceId: String): String!
  checkCalendarPermissions(roomId: String!): Room!

  putSpaceSaverRoomResizerExcludedRooms(companyId: ID!, roomIds: [ID!]!): SpaceSaverConfig!
  putSpaceSaverRoomResizerExcludedOrganizers(companyId: ID!, organizerEmails: [String!]!): SpaceSaverConfig!
  """
  Set specific rooms to have the setting specifically set to off.
  This is a convenience mutation to support current interface
  @see 'updateRoomConfig', 'updateRoomCheckInWindow', 'updateLocationRoomsCheckInWindow' for more advanced configurations
  """
  putSpaceSaverCheckInExcludedRooms(companyId: ID!, roomIds: [ID!]!): SpaceSaverConfig!
  """
  Sets specific meeting organizer emails for which check-in are not required (company-wide)
  """
  putSpaceSaverCheckInExcludedOrganizers(companyId: ID!, organizerEmails: [String!]!): SpaceSaverConfig!

  """
  Using separate mutations for each field rather than a generic updateLocationConfig because in the supergraph
  a mutation named 'updateLocationConfig' is ambiguous and these are all specific to Rooms
  This completely leaks implementation details breaking the Supergraph abstraction, but this is necessary since
  location based notification settings do not appear to be centralized
  """
  updateRoomsLocationShouldEmailGlobalAdmins(locationId: ID!, shouldEmailGlobalAdmins: Boolean!): Location!
  updateRoomsLocationShouldEmailLocationAdmins(locationId: ID!, shouldEmailLocationAdmins: Boolean!): Location!
  updateRoomsLocationNotificationEmails(locationId: ID!, notificationEmails: [String!]): Location!
  updateRoomsLocationRoomResizerEnabledOverride(locationId: ID!, roomResizerEnabledOverride: Boolean): Location!
  updateRoomsLocationOnsiteRoomRecaptureEnabledOverride(
    locationId: ID!
    onsiteRoomRecaptureEnabledOverride: Boolean
  ): Location!
  updateRoomsCompanyRoomResizerEnabled(companyId: ID!, roomResizerEnabled: Boolean!): Company!
  updateRoomsCompanyOnsiteRoomRecaptureEnabled(companyId: ID!, onsiteRoomRecaptureEnabled: Boolean!): Company!
  """
  Book a room now til a time that's suggested by backend
  """
  bookNow(
    roomId: ID!

    """
    A list of email addresses. The backend invites them to the meeting such that
    the meeting will show up on their calendars.
    """
    sendInviteTo: [String!]!
  ): Booking! @override(from: "ruby")
}

input NewInviteInput {
  additionalGuests: Int
  email: String

  """
  Employee ID
  """
  employee: ID
  expectedArrivalTime: DateTime!
  fullName: String!

  """
  Existing Invite ID
  """
  inviteId: ID

  """
  Location ID
  """
  location: ID!
  notifyVisitor: Boolean
  phone: String
  privateNotes: String
  reservationEndTime: DateTime
  reservationStartTime: DateTime
  userData: [FormFieldInput!]!
}

type NewUser {
  acceptedPrivacyPolicy: Boolean
  companyName: String
  email: String!
  fullName: String!
  id: ID!
  phoneNumber: String
}

type NotificationPreference {
  icon: String
  id: ID
  name: String!
  notificationPlatformKey: String!
  notificationType: String!
  preferenceValue: Boolean!
  relationId: ID!
  relationType: String!
}

type Organization {
  id: ID!
  locationBilling: Boolean!
  locations: [Location!]!
  name: String!
  role: UserRole
  subscription: ProductSubscription!
}

type Pagination {
  currentPage: Int!
  nextPage: Int
  size: Int!
  total: Int!
}

type PaginationDetails {
  limit: Int!
  offset: Int!
  total: Int!
}

type PassportStamp {
  large: String
  small: String
}

type PasswordReset {
  message: String!
}

enum PrivacyPolicyAcceptanceEnum {
  accepted
  expired
  not_accepted
}

type ProductSubscription {
  deliveries: SubscriptionDetails
  desks: SubscriptionDetails
  rooms: SubscriptionDetails
  vr: SubscriptionDetails
}

type Realm {
  id: ID!
  kind: String!
  name: String!
  parent: Realm
  roles: [Role!]
}

"""
A recipient is really just an Employee but with simplified fields
"""
type Recipient {
  """
  First + last name
  """
  formattedName: String
  id: ID!
}

"""
Day to register to come into the office
"""
type RegistrationDate {
  available: Boolean!
  date: DateTime!
  hasRequiredDocumentApproval: Boolean!
  id: ID!
  maxOccupancy: Int
  peopleRegistered: Int!
  requiredDocumentApprovalExpiresAt: DateTime
  requiredDocumentMessage: String
  reservation: Reservation
  screeningCard: ScreeningCardUnion!
}

"""
Valid invite and reservations associated with an office visit on a given day
"""
type RegistrationDatePartialDay {
  available: Boolean!
  date: DateTime!
  hasRequiredDocumentApproval: Boolean!
  id: ID!
  maxOccupancy: Int
  peopleRegistered: Int!
  requiredDocumentApprovalExpiresAt: DateTime
  requiredDocumentMessage: String
  reservations: [Reservation!]!
  screeningCard: ScreeningCardUnion!
}

type Reservation {
  bookingSource: String @deprecated(reason: "booking_source is no longer used.")
  canceledAt: DateTime
  cancelled: Boolean @deprecated(reason: "canceled_at field now used rather than cancelled.")
  checkInTime: DateTime
  checkOutTime: DateTime
  desk: Desk!
  employee: Employee
  endTime: Int
  entryId: ID
  id: ID!
  inviteId: ID
  isPartialDay: Boolean
  locationId: ID!
  startTime: Int
  userEmail: String
  userId: ID!
}

type ReservationResponse {
  invite: Invite
  reservation: Reservation!
}

type Role {
  id: ID!
  name: String!
  realm: Realm
}

type ScheduledEmployee {
  date: Date!
  desk: Desk
  email: String!
  employeeId: ID!
  favorite: Boolean!
  id: ID!
  name: String!
  profilePictureUrl: String
  scheduled: Boolean!
  signInStatus: Boolean!
}

type ScheduledEmployeeResults {
  hiddenScheduledEmployeesCount: Int!
  pagination: Pagination!
  scheduledEmployees: [ScheduledEmployee!]!
}

type ScheduledResources {
  date: Date!
  deskReservations: [Reservation!]!
  location: Location!
}

"""
Available scopes are 'mine' or 'boss'
"""
enum ScopeFilterEnum {
  """
  Fetch only resources where I am the host or boss is the host
  """
  boss

  """
  Fetch only resources where I am the creator or host
  """
  mine
}

"""
Represents either an Invite or SelfCertify
"""
union ScreeningCardUnion = Invite | SelfCertify | UserDocumentCard | WorkplaceVisitRequest

type SelfCertify {
  capacityReached: Boolean!
  email: String!
  expectedArrivalTime: DateTime!
  fullName: String!
  locationId: ID!
  userData: [FormField!]!
}

type SignInField {
  """
  Whether or not this field is displayed to visitors
  """
  allowVisitorRespondents: Boolean!
  dbField: String!
  identifier: String
  kind: SignInFieldKindEnum!
  localized: String
  name: String!
  options: [String!]!
  position: Int!
  required: Boolean!
}

enum SignInFieldKindEnum {
  email
  phone
  singleSelection
  text
}

type SignInFieldPage {
  enabled: Boolean!
  id: ID!
  position: Int!
  signInFields: [SignInField!]!
}

type SimplifiedSignIn {
  simplifiedSignIn: [RegistrationDatePartialDay!]!
}

"""
Represents either a SimplifiedSignIn or an Invite
"""
union SimplifiedSignInUnion = Invite | SimplifiedSignIn

type SubscriptionDetails {
  limitAlert: Alert
  limitExceeded: Boolean
  onTrial: Boolean!
  plan: String!
  subscribed: Boolean!
  trialEndDate: DateTime
}

type Ticket {
  attachments: [TicketAttachment!]
  description: String!
  fileAttachmentUrls: [ImageURISource!]
  id: ID!
  location: Location!
  locationDescription: TicketLocationDescription
  priority: TicketPriorityEnum!
  requestType: TicketRequestTypeEnum!
  requestor: User!
  subcategory: String
  ticketCategory: TicketCategory!
}

type TicketAttachment {
  """
  Attachment's file
  """
  file: ImageURISource!
  id: ID!
}

type TicketCategory {
  id: ID!
  name: String!
}

type TicketConfiguration {
  configurationCompleted: Boolean!
  enabled: Boolean!
  id: ID!
  ticketingIntegrationInstalled: Boolean!
  ticketingIntegrationName: String
}

"""
Location description source metadata
"""
type TicketLocationDescription {
  """
  Source Display
  """
  sourceDisplay: String!

  """
  Source ID
  """
  sourceId: ID
  sourceType: TicketLocationDescriptionSourceTypeEnum!
}

"""
Location description input data
"""
input TicketLocationDescriptionInput {
  """
  Source ID
  """
  sourceDisplay: String!

  """
  Source ID
  """
  sourceId: ID

  """
  Source type
  """
  sourceType: TicketLocationDescriptionSourceTypeEnum!
}

"""
Ticket location description source_type enum
"""
enum TicketLocationDescriptionSourceTypeEnum {
  """
  Represents the Deliveries product
  """
  deliveries

  """
  Represents the Desks product
  """
  desks

  """
  Represents the Rooms product
  """
  rooms

  """
  Represents free form text
  """
  text
}

"""
Ticket priorities
"""
enum TicketPriorityEnum {
  """
  High tickets
  """
  high

  """
  Low tickets
  """
  low

  """
  Normal tickets
  """
  normal

  """
  Urgent tickets
  """
  urgent
}

"""
Ticket request type options
"""
enum TicketRequestTypeEnum {
  """
  Incident
  """
  incident

  """
  Problem
  """
  problem

  """
  Question
  """
  question

  """
  Request
  """
  request
}

scalar Upload

"""
An Envoy User
"""
type User {
  acceptedPrivacyPolicy: Boolean!

  """
  Status of the auto sign-in push notification setting
  """
  autoSignInPushEnabled: Boolean!

  """
  Company name on the User Profile. Used by Passport to automatically fill in company name fields.
  Not to be mistaken for the name of the company relationship the user belongs to.
  """
  companyName: String

  """
  User's deliveries
  """
  deliveries(
    """
    Number of results returned per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int

    """
    Column to sort on, following JSON:API's convention. Eg 'sort=-status,created_at'
    """
    sort: String

    """
    The only two statuses that make sense in this context is 'pending' and 'collected'
    """
    status: [DeliveryStatus!]!
  ): [Delivery!]!

  """
  Deliveries scanned by the user
  """
  deliveriesScanned(
    """
    Number of results returned per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int

    """
    Column to sort on, following JSON:API's convention. Eg 'sort=-status,created_at'
    """
    sort: String

    """
    The only two statuses that make sense in this context is 'pending' and 'collected'
    """
    status: [DeliveryStatus!]
  ): [Delivery!]!
  email: String

  """
  User's employee record
  """
  employee: Employee

  """
  Entries for the current user
  """
  entries(
    """
    Pagination results per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int
  ): [Entry!]!

  """
  First + last name
  """
  formattedName: String
  hideFromEmployeeSchedule: Boolean!
  id: ID!

  """
  User's avatar
  """
  image: ImageURISource
  invites(
    """
    Date from filter
    """
    dateFrom: DateTime

    """
    Date to filter
    """
    dateTo: DateTime

    """
    Pagination results per page
    """
    limit: Int

    """
    Filter invites by location id
    """
    location: ID

    """
    Pagination page
    """
    page: Int
  ): [Invite!]!

  """
  Status of the invites push notification setting
  """
  invitesPushEnabled: Boolean!

  """
  The company the user belongs to. 'null' if the user is a Passport-only user.

  Note, in the following query,

  '''
  {
    me {
      organization {
        id
      }
    }
  }
  '''

  the organization will always reflect the company_id in the user's JWT. For Organizations deeper
  in the graph, the organization will be indeterministic due to the implementation of 'User#company'
  on 'envoy-web'.
  """
  organization: Organization

  """
  Status of the passport own locations setting
  """
  passportOwnLocations: Boolean!

  """
  Phone number
  """
  phoneNumber: String
  privacyPolicyAcceptance: PrivacyPolicyAcceptanceEnum

  """
  Status of the host push notification setting
  """
  pushNotificationEnabled: Boolean!
  realms: [Realm!]! @deprecated(reason: "A legacy concept from Identity")
  roles: [Role!]! @deprecated(reason: "A legacy concept from Identity")
}

type UserDocument {
  active: Boolean!
  approvalStatusForLocation: String
  id: ID!
  inputFieldsData: [InputFieldData!]!
  userDocumentAttachments: [UserDocumentAttachment!]!
  userDocumentLinks: [UserDocumentLink!]!
}

type UserDocumentAttachment {
  fileUrl: String!
  id: ID!
  userDocumentTemplateAttachment: UserDocumentTemplateAttachment
}

type UserDocumentCard {
  """
  Document status string: approved, denied, pending. Empty when not created
  """
  approvalStatus: String
  expirationDate: Date
  title: String!
  userDocumentId: ID
  userDocumentTemplateId: ID!
}

type UserDocumentLink {
  approvalStatus: String
  userDocumentLinkable: UserDocumentLinkable!
}

type UserDocumentLinkable {
  id: ID!
  type: String!
}

type UserDocumentTemplate {
  active: Boolean!
  description: String!
  id: ID!
  identifier: String!
  illustrationImageUrl: URL
  inputFields: [InputField!]!
  title: String!
  userDocumentTemplateAttachments: [UserDocumentTemplateAttachment!]!
  userDocuments(
    """
    User document ID filter
    """
    userDocumentId: ID
  ): [UserDocument!]!
}

type UserDocumentTemplateAttachment {
  description: String!
  id: ID!
  identifier: String!
  position: Int!
  required: Boolean!
  title: String!
}

type UserRole {
  confirmedAt: DateTime
  name: UserRoleName
}

"""
Represents all the possible user role values including company and location user roles
"""
enum UserRoleName {
  billing
  deliveries_admin
  employee
  global_admin
  location_admin
  receptionist
  security
}

type Visitor {
  avatarUrl: String
  email: String
  fullName: String
  phoneNumber: String
}

"""
status: a filter by the status - enum of: all, expected, signedIn, signedOut
expected = invites
signedIn = entries that are currently on-premise
signedOut = past entries
"""
enum VisitorSelectionEnum {
  all
  expected
  signedIn
  signedOut
}

"""
Represents either an Invite or Entry
"""
union VisitorUnion = Entry | Invite

"""
Wrapper for visitors query and pagination data
"""
type Visitors {
  """
  Visitors (invites and entries)
  """
  collection: [VisitorUnion!]!

  """
  Pagination metadata
  """
  pagination: VisitorsPagination!
}

type VisitorsPagination {
  """
  Pagination details for entries
  """
  entries: PaginationDetails!

  """
  Pagination details for invites as host
  """
  invites: PaginationDetails!

  """
  Current page
  """
  page: Int!
}

type WorkplaceDay {
  active: Boolean!
  dayOfWeek: String!
  endTime: String!
  id: ID!
  startTime: String!
}

type WorkplaceGroup {
  canEdit: Boolean!
  canEditMembers: Boolean!
  creator: Employee!
  id: ID!
  locked: Boolean!
  name: String!
  private: Boolean!
  workplaceGroupMembersResponse(
    """
    Limits the amount of records
    """
    limit: Int

    """
    Page for paginated records
    """
    page: Int
  ): WorkplaceGroupMembersResponse!
}

type WorkplaceGroupMember {
  employee: Employee!
  id: ID!
  role: String!
  workplaceGroupId: ID!
}

"""
Wrapper for workplace-group-members query and pagination data
"""
type WorkplaceGroupMembersResponse {
  """
  Workplace group members
  """
  collection: [WorkplaceGroupMember!]!

  """
  Pagination metadata
  """
  pagination: Pagination!
}

"""
Workplace Visit Request allows employees to invites other employees to visit a physical space together
"""
type WorkplaceVisitRequest {
  createdAt: DateTime!
  date: Date!
  id: ID!
  invitee: Employee!
  inviters: [Employee!]!
  location: Location!
  state: String!
  updatedAt: DateTime!
}

"""
Workplace Visit Request create response
"""
type WorkplaceVisitRequestCreateResponse {
  alreadyScheduledEmployeeIds: [ID!]
  createdWorkplaceVisitRequests: [WorkplaceVisitRequest!]!
}

scalar _Any

union _Entity =
    Amenity
  | Announcement
  | Carrier
  | Delivery
  | DeliveryArea
  | Desk
  | DesksLocation
  | DirectUpload
  | Door
  | Employee
  | EmployeeFavorite
  | EmployeeInvite
  | Entry
  | Feed
  | Floor
  | FloorReservationCount
  | Flow
  | IntegrationSetting
  | Invite
  | Location
  | NewUser
  | Organization
  | Realm
  | Recipient
  | RegistrationDate
  | RegistrationDatePartialDay
  | Reservation
  | Role
  | Room
  | ScheduledEmployee
  | Ticket
  | TicketAttachment
  | TicketCategory
  | TicketConfiguration
  | User
  | UserDocument
  | UserDocumentAttachment
  | UserDocumentLinkable
  | UserDocumentTemplate
  | UserDocumentTemplateAttachment
  | WorkplaceDay
  | WorkplaceGroup
  | WorkplaceGroupMember
  | WorkplaceVisitRequest

"""
The sdl representing the federated service capabilities. Includes federation
directives, removes federation types, and includes rest of full schema after
schema directives have been applied
"""
type _Service {
  sdl: String
}

type ProductFeature {
  enabled: Boolean
  availableChannels: [String]
}

type QuantityLimitedProductFeature {
  enabled: Boolean
  limit: Int
}

type DeskFeatures {
  enabled: Boolean

  permanentAssignment(locationId: ID): ProductFeature

  neighborhood(locationId: ID): ProductFeature

  amenities(locationId: ID): ProductFeature

  allowEnabling(locationId: ID): ProductFeature

  analytics(locationId: ID): ProductFeature

  csv(locationId: ID): ProductFeature

  accessDesksTab(locationId: ID): ProductFeature

  unlimitedDesks(locationId: ID): QuantityLimitedProductFeature
}

type ReservationFeatures {
  enabled: Boolean

  view(locationId: ID): ProductFeature

  booking(locationId: ID): ProductFeature

  partialDayBooking(locationId: ID): ProductFeature

  delegatedBooking(locationId: ID): ProductFeature

  bookingHours(locationId: ID): ProductFeature

  editing(locationId: ID): ProductFeature

  checkingIn(locationId: ID): ProductFeature

  canceling(locationId: ID): ProductFeature

  autoAssignment(locationId: ID): ProductFeature
}

type RoomFeatures {
  enabled: Boolean

  accessRoomsTab(locationId: ID): ProductFeature

  analytics(locationId: ID): ProductFeature

  unlimitedRooms(locationId: ID): QuantityLimitedProductFeature
}

type RoomMeetingFeatures {
  enabled: Boolean

  checkingIn(locationId: ID): ProductFeature

  maskNames(locationId: ID): ProductFeature

  notifications(locationId: ID): ProductFeature

  onSiteRoomRecapture(locationId: ID): ProductFeature

  recurringCleanup(locationId: ID): ProductFeature

  resizer(locationId: ID): ProductFeature

  serviceRequests(locationId: ID): ProductFeature
}

type AdminConfig {
  canViewAllReservations(locationId: ID!): ProductFeature
  canManageAllReservations(locationId: ID!): ProductFeature
}

type EditMapFeatures {
  enabled: Boolean

  attendanceAnalytics(locationId: ID): ProductFeature

  employeeSeating(locationId: ID): ProductFeature

  resourceAnalytics(locationId: ID): ProductFeature

  resourceOverview(locationId: ID): ProductFeature
}

type MapGQLFeatures {
  enabled: Boolean

  drafts(locationId: ID!): ProductFeature

  edit(locationId: ID!): EditMapFeatures

  view(locationId: ID!): ProductFeature
}

type CommunicationsFeature {
  enabled: Boolean
  employeeGroups(locationId: ID): ProductFeature
  notificationChannels(locationId: ID): ProductFeature
}

type FeatureConfig {
  adminConfig: AdminConfig
  communications(locationId: ID): CommunicationsFeature
  announcements(locationId: ID): ProductFeature

  deliveries(locationId: ID): ProductFeature

  desks(locationId: ID): DeskFeatures

  deskReservations(locationId: ID): ReservationFeatures

  rooms(locationId: ID): RoomFeatures

  roomMeetings(locationId: ID): RoomMeetingFeatures

  maps(locationId: ID): MapGQLFeatures

  deskAutoAssignment(locationId: ID): ProductFeature
  deskCheckIn(locationId: ID): ProductFeature
  desksActive(locationId: ID): ProductFeature
  desksOnMaps(locationId: ID): ProductFeature
  employeeRegistration(locationId: ID): ProductFeature
  employeePrivateData(locationId: ID): ProductFeature
  employeeScheduling(locationId: ID): ProductFeature
  employeeSignInStatus(locationId: ID): ProductFeature
  manageCustomRoles(locationId: ID): ProductFeature
  mapsPointOfInterest(locationId: ID): ProductFeature
  roomsOnMaps(locationId: ID): ProductFeature
  schedulingLimits(locationId: ID): ProductFeature
}

type Attendance {
  averageDaysPerWeek: Int
  meetsCompanyPolicy: Boolean
}

type Amenity {
  id: ID!
  name: String!
}

type Floor {
  building: String
  desks: [Desk!]!
  floorNumber: Int!
  floorPlanUrl: String
  id: ID!
  name: String!
}

type DeskUtilization {
  percentUtilized: Float
}

enum DeskAvailabilityType {
  available
  unavailable
  userAssigned
}

type MapFeatureInfo {
  floor: Floor
}

type Desk {
  accessible: Boolean!
  amenities: [Amenity!]
  assignedTo: String
  assignedEmployee: Employee
  enabled: Boolean!
  utilization(input: SpaceDashboardInput): DeskUtilization
  # (ar) I think this type should be DeskMapFeature
  mapFeature: MapFeatureInfo
  floor: Floor! @deprecated(reason: "Use the MapFeature and MapFloor types instead")
  floorId: ID!
  id: ID!
  name: String!
  neighborhood: String
  neighborhoodId: String
  deskAvailability: DeskAvailabilityEnum @deprecated(reason: "Use accessible and reservations fields instead")
  x: Float @deprecated(reason: "Desk geometry has been moved to the MapFeature type")
  y: Float @deprecated(reason: "Desk geometry has been moved to the MapFeature type")
  reservations(
    """
    End time for availability range
    """
    endTime: DateTime

    """
    Start time for availability range
    """
    startTime: DateTime
  ): [Reservation!]!
  status: DeskStatusEnum @deprecated(reason: "Use accessible and reservations fields instead")
}

type AssignedEmployee {
  id: ID
  name: String
  email: String
  department: String
  assignedDesk: Desk
  attendance: Attendance
}

type UnassignedEmployee {
  id: ID
  name: String
  email: String
  department: String
  attendance: Attendance
}

type InsightsMeta {
  numUnassignedMeetingAttendancePolicy: Int
  numAssignedMeetingAttendancePolicy: Int
  employeeAttendanceTrackingEnabled: Boolean
}

type AssignedAndUnassignedEmployeesMetaType {
  employeesPopulatedByScim: Boolean
}

type AssignedAndUnassignedEmployees {
  unassignedEmployees: [Employee]
  assignedEmployees: [Employee]
  meta: AssignedAndUnassignedEmployeesMetaType
}

interface Geometry {
  type: GeometryType!
}

enum GeometryType {
  point
  polygon
}

type Coordinate {
  x: Float
  y: Float
}

type Point implements Geometry {
  type: GeometryType!
  coordinate: Coordinate!
}

type Polygon implements Geometry {
  type: GeometryType!
  coordinates: [Coordinate!]!
  center: Coordinate
}

enum MapFeatureType {
  DESK
  ROOM
  VISITOR_AREA
  DELIVERY_AREA
  EMERGENCY_EXIT
  FIRST_AID_KIT
  AED
  FIRE_EXTINGUISHER
  KITCHEN
  CAFE
  RESTROOM
  MOTHERS_ROOM
  BREAK_ROOM
  PRINTER
  ELEVATOR
  STAIRS
  CUSTOM
}

interface MapFeatureInterface {
  id: ID!
  name: String
  notes: String
  enabled: Boolean!
  type: String!
}

type RoomUtilization {
  percentUtilized: Float
}

"""
Room id is usually in the email address format. id is globally unique
"""
type Room {
  id: ID!

  calendarId: String!
  claimable: Boolean!
  maskMeetingNames: Boolean
  description: String
  displayName: String

  locationId: ID

  companyId: ID!

  buildingId: ID

  utilization(input: SpaceDashboardInput): RoomUtilization

  """
  Things like TV, Zoom, Whiteboard
  Field is migrating from Ruby subgraph
  """
  amenities: [String!]! @override(from: "ruby")
  """
  Field is migrating from Ruby subgraph
  """
  capacity: Int @override(from: "ruby")
  """
  Room display name
  Field is migrating from Ruby subgraph
  """
  name: String! @override(from: "ruby")

  image: String @deprecated(reason: "Use imageUrl instead")
  """
  Full url to access the public image (used as background on tablets)
  """
  imageUrl: String
  syncStatus: RoomSyncStatus!

  """
  Room's devices
  """
  devices: [Device!]!

  """
  Room's location using data loader
  """
  location: Location

  company: Company

  building: Building

  """
  Room's status
  """
  status: RoomStatus

  """
  Setting override for company.checkInEnabled
  """
  checkInEnabledOverride: Boolean

  """
  The coalesced setting of checkInEnabledOverride and company.checkInEnabled
  """
  checkInEnabled: Boolean!

  """
  True if this setting overrides the location setting
  """
  preCheckInWindowIsOverride: Boolean!
  """
  Time window (in seconds) before meeting when users can check in
  """
  preCheckInWindow: Int!

  """
  True if this setting overrides the location setting
  """
  checkInWindowIsOverride: Boolean!
  """
  Time window (in seconds) after meeting start when users can check in
  """
  checkInWindow: Int!

  calendarPermissionsStatus: CalendarPermissionsStatus
  floor: String

  """
  Field is migrating from Ruby subgraph
  """
  backgroundImageUrl: URL @override(from: "ruby") @deprecated(reason: "Use imageUrl instead")

  """
  Distance to the available rooms
  Field is migrating from Ruby subgraph
  """
  proximity: BeaconProximity @override(from: "ruby")

  """
  Client will localize the duration to minutes/hours
  Field is migrating from Ruby subgraph
  """
  timeAvailableInMinutes: Int @override(from: "ruby")

  """
  The length of the booking is determined by rooms backend based on rooms availability and user preference
  Field is migrating from Ruby subgraph
  """
  timeToBookInMinutes: Int @override(from: "ruby")
}

type MapFloor {
  id: ID
  features(featureTypes: [MapFeatureType!], includeDisabled: Boolean): [MapFeatureInterface!]
}

type MapFeature implements MapFeatureInterface {
  id: ID!
  name: String
  notes: String
  enabled: Boolean!
  type: String!
  floor: Floor
}

type RoomMapFeature implements MapFeatureInterface {
  id: ID!
  name: String
  notes: String
  enabled: Boolean!
  type: String!
  room: Room
}

type DeskMapFeature implements MapFeatureInterface {
  id: ID!
  name: String
  notes: String
  assignedEmployee: Employee
  enabled: Boolean!
  type: String!
  desk: Desk
}

type WorkplaceAnalyticsOccupancyOnsiteMissionGrid {
  department: String
  deactivated: DateTime
  created: DateTime
  mission: Int
  period: DatePeriod
  mandatoryDays: [WeekDay]
}

type EmployeeAttendanceInsights {
  meetsAttendancePolicy: Boolean
}

type EmployeeAttendancePerformanceBasic {
  averageWeeklyAttendance: Float!
  onsiteMission: WorkplaceAnalyticsOccupancyOnsiteMissionGrid
  insights: EmployeeAttendanceInsights
}

type Employee {
  email: String
  favorite: Boolean!
  hideFromEmployeeSchedule: Boolean!
  id: Int!
  name: String!
  phoneNumber: String
  profilePictureUrl: String
  signInStatus: Boolean!
  locations: [Location!]!
  department: String
  assignedDesks: [Desk]
  workplaceGroups: [WorkplaceGroup!]!
  attendancePerformance(input: SpaceDashboardInput!): EmployeeAttendancePerformanceBasic
}

type AreaMap {
  id: ID
  floors(ids: [ID]): [MapFloor!]
}

"""
Exposes a URL that specifies the behaviour of this scalar.
"""
directive @specifiedBy(
  """
  The URL that specifies the behaviour of this scalar.
  """
  url: String!
) on SCALAR

"""
An RFC-3339 compliant DateTime Scalar
"""
scalar DateTime @specifiedBy(url: "https://scalars.graphql.org/andimarek/date-time.html")

"""
Long type
"""
scalar Long

"""
URL type
"""
scalar URL

"""
Validates field access to admins
"""
directive @AdminOnly on FIELD_DEFINITION

"""
Space separated list of primary keys needed to access federated object
"""
directive @key(fields: _FieldSet!) on OBJECT | INTERFACE

"""
Marks target field as external meaning it will be resolved by federated schema
"""
directive @external on FIELD_DEFINITION

"""
Marks target field or type as not appearing in the federated schema
"""
directive @internal on OBJECT | FIELD_DEFINITION

"""
Specifies required input field set from the base type for a resolver
"""
directive @requires(fields: _FieldSet!) on FIELD_DEFINITION

"""
Specifies the base type field set that will be selectable by the gateway
"""
directive @provides(fields: _FieldSet!) on FIELD_DEFINITION

"""
Marks target object as extending part of the federated schema
"""
directive @extends on OBJECT | INTERFACE

directive @override(from: String!) on FIELD_DEFINITION

"""
Federation type representing set of fields
"""
scalar _FieldSet

enum BluetoothStatusType {
  ALLOWED
  DENIED
  UNKNOWN
}

type CalendarEvent {
  description: String
  endDatetime: DateTime!
  id: Long!
  originalEndTimezone: String!
  originalStartTimezone: String!
  roomId: String!
  startDatetime: DateTime!
  summary: String
  organizerEmail: String
  attendeeEmails: [String!]
  checkIn: CheckIn
}

input CalendarEventInput {
  description: String
  endDatetime: DateTime!
  organizer: String
  roomId: ID!
  startDatetime: DateTime!
  summary: String
  timezone: String!
  attendeeEmails: [String!]
}

input CalendarEventsFilterInput {
  endDatetime: DateTime!
  roomId: ID!
  startDatetime: DateTime!
}

enum ChargingStatusType {
  FULL
  CHARGING
  UNPLUGGED
  UNKNOWN
}

enum TimeFormat {
  STANDARD
  MILITARY
}

input SendSlackCheckInInput {
  calendarEventId: Long!
  roomId: ID!
}

input CheckInInput {
  calendarEventId: Long!
  roomId: ID
}

input CheckOutInput {
  calendarEventId: Long!
  roomId: ID
}

input MarkEventMissedInput {
  calendarEventId: Long!
  roomId: ID!
  missedAt: DateTime!
}

type Company @key(fields: "id") {
  id: ID!
  name: String
  cancelUnattendedRecurringMeeting: Boolean!
  pairingCode: String!
  roomCheckInEnabled: Boolean!
  settingsAccessCode: String!
  roomResizerEnabled: Boolean!
  onsiteRoomRecaptureEnabled: Boolean!

  """
  Company's connections (oauth pairings) using data loader
  """
  connections: [Connection!]!

  """
  Company's locations using data loader
  """
  locations: [Location!]!

  billingRoomsSubscription: BillingRoomsSubscription

  """
  For rooms
  """
  spaceSaverConfig: SpaceSaverConfig
  devicesTimeFormat: TimeFormat!

  """
  Time window (in seconds) before meetings start, when users can check in
  """
  roomsPreCheckInWindow: Int!
  """
  Time window (in seconds) after meetings start, when users can check in
  """
  roomsCheckInWindow: Int!
}

type SpaceSaverConfig @internal {
  companyId: ID!
  roomResizerExcludedRooms: [RoomResizerExcludedRoom!]!
  roomResizerExcludedOrganizers: [String!]!
  """
  These are rooms that have the check-in option overwritten to off.
  For advanced configs @see 'checkInEnabledOverride'
  """
  checkInExcludedRooms: [Room!]!
  """
  List of organizer emails for which the meetings don't require checking in
  """
  checkInExcludedOrganizers: [String!]!
}

type RoomResizerExcludedRoom @internal {
  roomId: ID!
  room: Room
}

type BillingRoomsSubscription @internal {
  cancelled: Boolean!
  quantity: Int!
  features: [String!]!
}

input CompanyConfigInput {
  cancelUnattendedRecurringMeeting: Boolean
  roomCheckInEnabled: Boolean
  settingsAccessCode: String
  roomResizerEnabled: Boolean
  onsiteRoomRecaptureEnabled: Boolean
  devicesTimeFormat: TimeFormat
}

input CompanyInput {
  id: ID!
  name: String!
  pairingCode: String!
}

type Connection {
  id: ID!
  email: String!
  provider: OauthProvider!
  roomsCount: Int
}

type Device @key(fields: "id") {
  id: ID!
  appVersion: String
  batteryLevel: Int!
  bluetoothStatus: BluetoothStatusType!
  chargingStatus: ChargingStatusType!
  clientId: String
  jackStatus: JackStatusType!
  ledAudioType: LightedCaseType!
  lightbarDetected: Boolean!
  model: String
  name: String!
  offlineAt: DateTime
  osVersion: String
  volume: Int!
  lastOnlineAt: DateTime
  companyId: ID!

  roomId: ID
  """
  Device's room using data loader
  """
  room: Room
}

input DevicesFilterInput {
  ids: [ID!]
  roomId: ID
}

type DeviceToken {
  accessToken: String!
  expiresIn: Long!
  refreshToken: String
  scope: String!
  tokenType: String!
}

enum JackStatusType {
  PLUGGED
  UNPLUGGED
  UNKNOWN
}

enum LightedCaseType {
  VISUAL_TARGET
  GENERIC
  NONE
}

input LocationInput {
  companyId: ID!
  id: ID!
  name: String!
}

enum OauthProvider {
  GOOGLE
  MICROSOFT_USER
  MICROSOFT_SERVICE
}

type Building @key(fields: "id") {
  id: ID!
  companyId: ID!
  name: String
  description: String
  latitude: String
  longitude: String
  postalCode: String
  region: String
  address: String
}

type BuildingFilterOption {
  name: String
  floors: [String!]!
}

type CalendarPermissionsStatus {
  hasReadPermissions: Boolean!
  hasWritePermissions: Boolean!
  permissionsLastCheckedAt: DateTime!
}

enum RoomAvailability {
  OCCUPIED
  AVAILABLE
  PENDING @deprecated(reason: "Historically used for check-in status, now just use the checkin field instead.")
}

input RoomConfigInput {
  claimable: Boolean
  maskMeetingNames: Boolean
  checkInEnabledOverride: Boolean
  id: ID
  calendarId: ID
  calendarEmail: String
  displayName: String
}

input RoomCheckInWindowInput {
  id: ID!
  """
  Null to use default location setting
  """
  preCheckInWindowOverride: Int
  """
  Null to use default location setting
  """
  checkInWindowOverride: Int
}

input RoomsFilterInput {
  ids: [ID!]
  locationId: ID
}

enum SortDirection {
  ASC
  DESC
}

input PaginationInput {
  limit: Int!
  offset: Int!
}

"""
Only one of the operations will apply. If none is set, it'll be ignored
"""
input IDFilter {
  _eq: ID
  _neq: ID
  _in: [ID!]
  _nin: [ID!]
  """
  True if it should return all records with the specified field null
  """
  _is_null: Boolean
}

"""
Only one of the operations will apply. If none is set, it'll be ignored
"""
input StringFilter {
  _eq: String
  _neq: String
  _in: [String!]
  _nin: [String!]
  """
  Filter content containing the provided string (case insensitive)
  """
  _ilike: String
  """
  Filter content that doesn't contain the provided string (case insensitive)
  """
  _nilike: String
  """
  True if it should return all records with the specified field null
  """
  _is_null: Boolean
}

input RoomsPageFilterInput {
  locationId: IDFilter
  buildingId: IDFilter
  """
  We need to be able to filter by building name instead of id, because on Microsoft each room has a building associated to it
  """
  buildingName: IDFilter
  """
  Results will include rooms that have a floor matching the provided text
  """
  floorName: IDFilter
  """
  Results will include rooms that have a name containing this text (case-insensitive)
  """
  displayName: StringFilter
}

enum RoomsOrderBy {
  DISPLAY_NAME
  LOCATION_NAME
  BUILDING_NAME
  FLOOR_NAME
}

input RoomsSortOrder {
  by: RoomsOrderBy!
  direction: SortDirection!
}

type PaginatedRoomsResponse {
  items: [Room!]!
  totalCount: Int!
}

type RoomStatus {
  currentCalendarEventId: Long
  status: RoomAvailability!
  time: DateTime!
}

enum RoomSyncStatus {
  SYNC_NOT_STARTED
  SYNC_STARTED
  SYNC_COMPLETED
}

type Booking {
  endTime: DateTime! @override(from: "ruby")
  room: Room! @override(from: "ruby")
  startTime: DateTime! @override(from: "ruby")
}

type Query {
  company: Company!
  devices(filter: DevicesFilterInput!): [Device!]!
  location(id: ID!): Location!
  room(id: ID!): Room!
  rooms(filter: RoomsFilterInput!): [Room!]!
  paginatedRooms(
    filter: RoomsPageFilterInput!
    sort: RoomsSortOrder!
    pagination: PaginationInput
  ): PaginatedRoomsResponse!
  building(id: ID!): Building!
  """
  Full list of building names and floor names available for the company to be used as filters
  """
  buildingFilterOptions: [BuildingFilterOption!]!
  calendarEvents(filter: CalendarEventsFilterInput!): [CalendarEvent!]!
  getDevicePairingStatus(id: Long!): DevicePairingStatus!
  introspectDeviceToken(accessToken: String): Device
  getDevicePairingCodeByUserCode(userCode: String!): DevicePairingCode
  spaceSaverConfig: SpaceSaverConfig
  """
  Available rooms
  """
  availableRooms(
    """
    Filter available rooms by location id
    """
    location: ID
    nearbyBeacons: [BeaconInput!]
  ): [Room!]! @override(from: "ruby")

  _entities(representations: [_Any!]!): [_Entity]!
  _service: _Service!

  """
  All amenities at a locations
  """
  amenitiesForLocation(
    """
    Location ID parameter
    """
    locationId: ID!
  ): [Amenity!]!

  """
  Announcements by ID
  """
  announcement(
    """
    Announcement ID
    """
    id: ID!
  ): Announcement!

  """
  Announcements for a location
  """
  announcements(
    """
    Location ID filter
    """
    locationId: ID!
  ): [Announcement!]!

  """
  Available desks for a location, user is pulled from jwt
  """
  availableDesksForLocation(
    """
    Date for availability
    """
    date: DateTime!

    """
    Location ID parameter
    """
    locationId: ID!
  ): [Desk!]!
    @deprecated(
      reason: "Migrate to loading desks by floor with availableDesksOnFloorForLocation' for improved error reporting"
    )

  """
  Available desks for a location, user is pulled from jwt
  """
  availableDesksOnFloorForLocation(
    """
    Date for availability
    """
    date: DateTime!

    """
    Floor ID to filter by
    """
    floorId: ID!

    """
    Location ID parameter
    """
    locationId: ID!
  ): AvailableDesksOnFloor!

  """
  List of floors for the location, optionally eager loading desks for a floor
  """
  availableFloorsForLocation(
    """
    Optional date filter for availability, if used floor_id is also required, and will eager loads desks for floor
    """
    date: DateTime

    """
    Optional Floor ID, if used date is also required, and will eager loads desks for floor
    """
    floorId: ID

    """
    Location ID Filter
    """
    locationId: ID!
  ): AvailableFloors!

  """
  List of employees at a given company
  """
  bulkEmployeesByEmail(
    employeeEmails: [String!]!
    limit: Int

    """
    Filter by location id
    """
    locationId: ID
    page: Int
  ): BulkEmployeesByEmail!

  """
  Office cards
  """
  cards(
    """
    Pagination limit
    """
    limit: Int

    """
    Filter by location id
    """
    location: ID

    """
    Scope filter for entries and invites
    """
    scope: ScopeFilterEnum
  ): [CardUnion!]!

  """
  All carriers
  """
  carriers: [Carrier!]!

  """
  Deliveries viewable by user
  """
  deliveries(
    """
    The delivery area ID (Deprecated, use 'deliveryAreas' instead)
    """
    deliveryArea: ID

    """
    Delivery area IDs
    """
    deliveryAreas: [ID!]

    """
    Delivery IDs
    """
    id: [ID!]

    """
    Results per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int

    """
    The employee ID (type should really be ID)
    """
    recipient: String

    """
    JSON-API sort
    """
    sort: String

    """
    Delivery status
    """
    status: [DeliveryStatus!]
  ): [Delivery!]!

  """
  Delivery by ID
  """
  delivery(
    """
    The delivery ID
    """
    id: ID!
  ): Delivery!

  """
  Delivery Area by ID
  """
  deliveryArea(
    """
    The delivery area ID
    """
    id: ID!
  ): DeliveryArea!

  """
  Delivery Areas by status
  """
  deliveryAreas(
    """
    Results per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int

    """
    Realm ID
    """
    realms: [ID!]

    """
    Delivery area enabled-ness
    """
    status: [ActiveStatus!]
  ): [DeliveryArea!]!

  """
  Desks for a location on the floor
  """
  desksOnFloorForLocation(
    """
    Date for availability (DEPRECATED)
    """
    date: DateTime

    """
    End time for availability range
    """
    endTime: DateTime

    """
    Desk Filter to filter by available, anonymous, unavailable, booked status
    """
    filters: [DeskStatusEnum!]!

    """
    Floor ID to filter by
    """
    floorId: ID!

    """
    Location ID parameter
    """
    locationId: ID!

    """
    Start time for availability range
    """
    startTime: DateTime
  ): DesksOnFloor!

  """
  Doors integration setting to determine availability
  """
  doorsIntegrationSetting(
    """
    Location ID parameter
    """
    locationId: ID!
  ): IntegrationSetting

  """
  Employee by ID
  """
  employee(
    """
    Employee ID
    """
    employeeId: ID!

    """
    Current location ID
    """
    locationId: ID
  ): Employee!

  """
  Returns one desk reservation for a fellow employee on a given date
  """
  employeeDesk(
    """
    Target date parameter
    """
    date: DateTime!

    """
    Employee ID parameter
    """
    employeeId: ID!

    """
    Location ID parameter
    """
    locationId: ID!
  ): Reservation!
    @deprecated(reason: "Migrate to 'employee_desks' since an employee can have multiple reservations for a day")

  """
  Returns desk reservations for a fellow employee for a given start and end time
  """
  employeeDesks(
    """
    Employee ID parameter
    """
    employeeId: ID!

    """
    Start time parameter
    """
    endTime: DateTime!

    """
    Location ID parameter
    """
    locationId: ID!

    """
    Start time parameter
    """
    startTime: DateTime!
  ): EmployeeDesks!

  """
  List of favorited employees
  """
  employeeFavorites(
    """
    Results per page
    """
    limit: Int

    """
    Pagination page
    """
    page: Int
  ): [EmployeeFavorite!]!

  """
  Registration information for multi-day registration
  """
  employeeRegistration(
    """
    End date of window
    """
    endDate: DateTime!

    """
    Location ID Filter
    """
    locationId: ID!

    """
    Start date of window
    """
    startDate: DateTime!
  ): EmployeeRegistration!

  """
  Registration information for multi-day registration and partial day reservations
  """
  employeeRegistrationPartialDay(
    """
    End date of window
    """
    endDate: DateTime!

    """
    Location ID Filter
    """
    locationId: ID!

    """
    Start date of window
    """
    startDate: DateTime!
  ): EmployeeRegistrationPartialDay!

  """
  Employee screening cards for self-certification
  """
  employeeScreeningCards(
    """
    Location ID Filter
    """
    locationId: ID!
  ): [ScreeningCardUnion!]!

  """
  All employees viewable by user
  """
  employees(
    """
    Results per page
    """
    limit: Int

    """
    Filter by location IDs
    """
    locations: [ID!]

    """
    Pagination page
    """
    page: Int

    """
    Filter the employees whose name contains the given value
    """
    query: String

    """
    JSON-API sort
    """
    sort: String

    """
    Filter employees who belong to groups IDs
    """
    workplaceGroups: [ID!]
  ): [Employee!]!

  """
  All entries
  """
  entries(
    """
    Filter by list of comma-separated employee IDs as hosts
    """
    employee: String

    """
    Pagination results per page
    """
    limit: Int

    """
    Filter entries by location id
    """
    location: Int

    """
    Pagination page
    """
    page: Int

    """
    Filter by scope (mine, boss)
    """
    scope: String

    """
    Entry possible statuses
    """
    status: EntryStatusEnum
  ): [Entry!]!

  """
  Entry by ID
  """
  entry(
    """
    Entry ID
    """
    id: ID!
  ): Entry!

  """
  Configurations for various envoy products
  """
  envoyAccountConfig: EnvoyAccountConfig!

  """
  List of floors with reservation count
  """
  floorReservationCounts(
    """
    Optional date filter for reservations
    """
    date: DateTime

    """
    Location ID Filter
    """
    locationId: ID!
  ): FloorReservationCounts!

  """
  List of floors with desks
  """
  floorsAndDesksForLocation(
    """
    Location ID Filter
    """
    locationId: ID!
  ): [Floor!]!

  """
  Get reservations that match the provided filters
  """
  getReservations(
    """
    End time parameter
    """
    endTime: DateTime

    """
    Floor ID parameter
    """
    floorId: ID

    """
    Limit parameter
    """
    limit: Int

    """
    Location ID parameter
    """
    locationId: ID!

    """
    Pagination parameter
    """
    page: Int

    """
    Start time parameter
    """
    startTime: DateTime
  ): GetReservations!

  """
  Invite by ID
  """
  invite(
    """
    Invite ID
    """
    id: ID!
  ): Invite!

  """
  All invites
  """
  invites(
    """
    Filter by an Employee ID
    """
    employee: ID

    """
    Pagination limit argument
    """
    limit: Int

    """
    Filter invites by location id
    """
    location: Int!

    """
    Pagination page number argument
    """
    page: Int

    """
    [Deprecated] Filter by scope (mine, boss)
    """
    scope: String

    """
    JSON-API sort
    """
    sort: String
  ): [Invite!]!

  """
  List of doors by location_id
  """
  listDoors(
    """
    Location ID parameter
    """
    locationId: ID!
  ): [Door!]!

  """
  All locations user in session has access to
  """
  locations(
    """
    Filter disabled vs enabled locations, considering the status of the company as well
    """
    disabled: Boolean

    """
    Filter disabled vs enabled locations, considering only the status of the location
    """
    disabledLocation: Boolean

    """
    Location IDs
    """
    id: [ID!]
  ): [Location!]!

  """
  User scoped by the current JWT
  """
  me: User!

  """
  Notification preferences
  """
  notificationPreferences: [NotificationPreference!]!

  """
  Returns single Reservation, with Desk included
  """
  reservationById(
    """
    Reservation ID parameter
    """
    reservationId: ID!
  ): Reservation!

  """
  List of dates an given employee is scheduled at including the location
  """
  scheduledDatesForEmployee(
    """
    Employee ID filter
    """
    employeeId: ID!
  ): [ScheduledResources!]!

  """
  List of protect scheduled employees on a given date
  """
  scheduledEmployees(
    """
    Target date parameter
    """
    date: Date!

    """
    Location ID parameter
    """
    locationId: ID!

    """
    Pagination page number
    """
    page: Int

    """
    Fuzzy name search string
    """
    query: String

    """
    Allows filtering by scheduled attribute. Default: true
    """
    scheduled: Boolean

    """
    Pagination page size
    """
    size: Int

    """
    Filter scheduled employees who belong to groups IDs
    """
    workplaceGroups: [ID!]
  ): ScheduledEmployeeResults!

  """
  List of configured ticket categories by location ID
  """
  ticketCategories(
    """
    Location ID parameter
    """
    locationId: ID!
  ): [TicketCategory!]!

  """
  List of the active user documents
  """
  userDocumentTemplates(
    """
    Location ID filter
    """
    locationId: ID!

    """
    User document template ID filter
    """
    userDocumentTemplateId: ID
  ): [UserDocumentTemplate!]

  """
  Visitors collection and pagination data
  """
  visitors(
    """
    End time for invites and/or entries
    """
    endTime: DateTime

    """
    Entry scope: mine, boss
    """
    entriesScope: EntryScopeEnum

    """
    Filter by location id
    """
    locationId: ID!

    """
    Pagination limit
    """
    page: Int

    """
    Status for invites or entries (all|expected|signedIn|signedOut)
    """
    selection: VisitorSelectionEnum

    """
    Start time for invites and/or entries
    """
    startTime: DateTime
  ): Visitors!

  """
  WorkplaceGroup by ID
  """
  workplaceGroup(
    """
    WorkplaceGroup ID parameter
    """
    id: ID!

    """
    Filter by location id
    """
    locationId: ID
  ): WorkplaceGroup!

  """
  List of workplace groups an employee belongs to
  """
  workplaceGroups(
    """
    Employee ID parameter
    """
    employeeId: ID!

    """
    Filter by location id
    """
    locationId: ID
  ): [WorkplaceGroup!]!

  featureConfig: FeatureConfig
  getAssignedAndUnassignedEmployees(input: GetAssignedAndUnassignedEmployeesInput!): AssignedAndUnassignedEmployees
  areaMap(id: ID!): AreaMap
  assignments(input: AssignmentFilterInput!): [Assignment!]!
}

enum WeekDay {
  MON
  TUE
  WED
  THU
  FRI
  SAT
  SUN
}

enum DatePeriod {
  WEEK
  MONTH
}

input SpaceFilterInput {
  companyIds: [ID!]
  locationIds: [ID!]
  start: DateTime
  end: DateTime
}

input SpaceSettingsInput {
  weekdays: [WeekDay]
}

input AssignmentFilterInput {
  locationId: ID!
}

input SpaceDashboardInput {
  filter: SpaceFilterInput
  setting: SpaceSettingsInput
}

input GetAssignedAndUnassignedEmployeesInput {
  locationId: ID!
  draftId: ID
}

input ExtendEventInput {
  calendarEventId: Long!
  minutesToAdd: Long!
}

input DeleteEventInput {
  calendarEventId: Long!
}

type Assignment {
  id: ID!
  employee: Employee
  deskId: ID!
  desk: Desk
  startTime: DateTime!
  scheduledBy: Employee
}

type DevicePairingCode {
  userCode: String!
  deviceCode: String!
  interval: Long!
  expiresAt: DateTime!
  redirectUri: String!
}

type DevicePairingStatus {
  id: Long!
  interval: Long!
  expiresAt: DateTime!
  deviceId: ID
  device: Device
}

type CheckIn {
  calendarEventId: Long!
  startDatetime: DateTime!
  startTimezone: String!
  checkInWindowMinutes: Int!
  checkInAt: DateTime
  processedAt: DateTime
}

type ErrorList {
  errors: [String!]!
}
`;

export default federatedSchema;
